import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './VerifyBooking.css';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import apiClient from '../../services/api';
import { Search, User, Phone, Calendar, CreditCard, CheckCircle, Receipt, Building2, XCircle } from 'lucide-react';

const VerifyBooking = () => {
    const location = useLocation();
    const prefillCode = location.state?.prefill || '';
    const [searchCode, setSearchCode] = useState(prefillCode);
    const [bookingData, setBookingData] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [isCheckedIn, setIsCheckedIn] = useState(false);
    const [isMarkingPaid, setIsMarkingPaid] = useState(false);
    const [currentBookingId, setCurrentBookingId] = useState(null);

    // Auto-search if a code was passed via navigation
    useEffect(() => {
        if (prefillCode) {
            const syntheticEvent = { preventDefault: () => {} };
            handleSearch(syntheticEvent);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchCode.trim()) return;
        
        setIsSearching(true);
        setIsCheckedIn(false);
        setBookingData(null);

        try {
            // Send pure code (alphanumeric clean up done backend side if #BK- prefixed)
            const res = await apiClient.get(`/bookings/verify/${encodeURIComponent(searchCode)}`);
            if (res.data?.data) {
                const b = res.data.data;
                const checkInDate = new Date(b.check_in).toLocaleDateString();
                const checkOutDate = new Date(b.check_out).toLocaleDateString();
                
                setIsCheckedIn(!!b.verification_used_at);
                
                setCurrentBookingId(b.id);

                // Parse add-on services and second room
                let rawServices = [];
                let addOns = [];
                let secondRoom = null;
                let addOnsAmount = 0;
                if (b.extra_services) {
                    try {
                        rawServices = Array.isArray(b.extra_services) ? b.extra_services : JSON.parse(b.extra_services);
                        rawServices.forEach(addon => {
                            if (addon.service_type === 'second_room') {
                                secondRoom = addon;
                            } else {
                                addOns.push(addon);
                                addOnsAmount += parseFloat(addon.price_at_booking || addon.price || 0) * (addon.quantity || 1);
                            }
                        });
                    } catch {}
                }

                // Compute nights
                const d1 = new Date(b.check_in);
                const d2 = new Date(b.check_out);
                let nightsCount = Math.max(1, Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)));
                if (b.entity_type === 'Car' && nightsCount === 1 && d1.toDateString() === d2.toDateString()) {
                    nightsCount = 1; 
                }

                const totalAmountVal = parseFloat(b.total_price || 0);
                const baseAmount = totalAmountVal - addOnsAmount - (secondRoom ? parseFloat(secondRoom.price_at_booking || 0) : 0);
                const amountPaidVal = parseFloat(b.amount_paid !== undefined ? b.amount_paid : b.total_price || 0);
                const remainingBalanceVal = b.payment_plan === 'Partial' ? Math.max(0, totalAmountVal - amountPaidVal) : 0;

                setBookingData({
                    id: `#BK-${b.id}`,
                    rawId: b.id,
                    guestName: b.guest_name || 'Guest',
                    phone: b.guest_phone || 'N/A',
                    email: b.guest_email || 'N/A',
                    optionalGuestName: secondRoom?.guest_name || b.second_guest_name || '',
                    secondRoom: secondRoom,
                    room: b.unit_number ? `Room ${b.unit_number} (${b.unit_type || 'Standard'})` : (b.entity_name || 'Unit'),
                    checkIn: checkInDate,
                    checkOut: checkOutDate,
                    arrivalTime: b.arrival_time || '14:00',
                    guests: b.guests || 2,
                    totalAmount: `${totalAmountVal.toFixed(2)}`,
                    amountPaid: `${amountPaidVal.toFixed(2)}`,
                    remainingBalance: `${remainingBalanceVal.toFixed(2)}`,
                    paymentPlan: b.payment_plan || 'Full',
                    paymentStatus: b.payment_status || 'Unpaid',
                    bookingStatus: b.status || 'Pending',
                    addOns: addOns,
                    nightsCount: nightsCount,
                    baseAmount: baseAmount,
                    addOnsAmount: addOnsAmount,
                });
            } else {
                setBookingData('not_found');
            }
        } catch (error) {
            setBookingData('not_found');
        } finally {
            setIsSearching(false);
        }
    };

    const handleCheckIn = async () => {
        try {
            await apiClient.post(`/bookings/checkin/${encodeURIComponent(searchCode)}`);
            setIsCheckedIn(true);
        } catch (error) {
            alert('Failed to check in: ' + (error.response?.data?.message || 'Unauthorized'));
        }
    };

    const handleMarkAsPaid = async () => {
        if (!currentBookingId) return;
        setIsMarkingPaid(true);
        try {
            await apiClient.patch(`/bookings/${currentBookingId}/mark-paid`);
            setBookingData(prev => ({ ...prev, paymentStatus: 'Paid' }));
            alert('Booking marked as paid successfully! The client\'s receipt will now show PAID.');
        } catch (error) {
            alert('Failed to mark as paid: ' + (error.response?.data?.message || 'Unauthorized'));
        } finally {
            setIsMarkingPaid(false);
        }
    };

    return (
        <div className="verify-page-container">
            <Sidebar />
            <main className="main-content">
                <div className="verify-top-row">
                    <div className="verify-title-block">
                        <h1>Verify Booking</h1>
                        <p>Enter the booking code from the guest's receipt to view details and check them in.</p>
                    </div>
                </div>

                <div className="verify-content-wrapper">
                    {/* Search Section */}
                    <div className="search-section card-box">
                        <h2>Search by Code</h2>
                        <form onSubmit={handleSearch} className="search-form">
                            <div className="verify-search-input-wrapper">
                                <Search size={20} className="search-icon" />
                                <input 
                                    type="text" 
                                    placeholder="e.g. #BK-9021" 
                                    value={searchCode}
                                    onChange={(e) => setSearchCode(e.target.value)}
                                    className="verify-search-input"
                                />
                            </div>
                            <button type="submit" className="verify-search-btn" disabled={isSearching}>
                                {isSearching ? 'Searching...' : 'Find Booking'}
                            </button>
                        </form>
                    </div>

                    {/* Results Section */}
                    {bookingData === 'not_found' && (
                        <div className="result-section not-found card-box">
                            <XCircle size={40} className="not-found-icon" color="#ef4444" />
                            <h3>Booking Not Found</h3>
                            <p>No booking matches the code "{searchCode}". Please check the receipt and try again.</p>
                        </div>
                    )}

                    {bookingData && bookingData !== 'not_found' && (
                        <div className="result-section found card-box">
                            <div className="receipt-header">
                                <div className="receipt-title">
                                    <Receipt size={24} color="#0288AC" />
                                    <h2>Booking Details</h2>
                                </div>
                                <div className="receipt-badge">
                                    {isCheckedIn ? 'Checked In' : bookingData.paymentStatus}
                                </div>
                            </div>

                            <div className="receipt-grid">
                                <div className="receipt-card-section">
                                    <h3>Guest Information</h3>
                                    <div className="info-row">
                                        <User size={16} className="info-icon" />
                                        <div>
                                            <p className="info-label">Primary Guest</p>
                                            <p className="info-value">{bookingData.guestName}</p>
                                        </div>
                                    </div>
                                    <div className="info-row">
                                        <Phone size={16} className="info-icon" />
                                        <div>
                                            <p className="info-label">Phone Number</p>
                                            <p className="info-value">{bookingData.phone}</p>
                                        </div>
                                    </div>
                                    {bookingData.optionalGuestName && (
                                        <div className="info-row" style={{ marginTop: '12px', borderTop: '1px dashed #E2E8F0', paddingTop: '8px' }}>
                                            <User size={16} className="info-icon" color="#0288AC" />
                                            <div>
                                                <p className="info-label" style={{ color: '#0288AC', fontWeight: '700' }}>Guest for Room 2 (Optional Name)</p>
                                                <p className="info-value" style={{ fontWeight: '700', color: '#0F172A' }}>{bookingData.optionalGuestName}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="receipt-card-section">
                                    <h3>Stay Details</h3>
                                    <div className="info-row">
                                        <Building2 size={16} className="info-icon" />
                                        <div>
                                            <p className="info-label">Unit / Room</p>
                                            <p className="info-value">{bookingData.room}</p>
                                        </div>
                                    </div>
                                    <div className="info-row">
                                        <Calendar size={16} className="info-icon" />
                                        <div>
                                            <p className="info-label">Dates</p>
                                            <p className="info-value">{bookingData.checkIn} - {bookingData.checkOut}</p>
                                        </div>
                                    </div>
                                    {bookingData.arrivalTime && (
                                        <div className="info-row">
                                            <Calendar size={16} className="info-icon" color="#0288AC" />
                                            <div>
                                                <p className="info-label">Estimated Arrival</p>
                                                <p className="info-value">{bookingData.arrivalTime}</p>
                                            </div>
                                        </div>
                                    )}
                                    {bookingData.secondRoom && (
                                        <div className="info-row" style={{ marginTop: '12px', borderTop: '1px dashed #E2E8F0', paddingTop: '8px' }}>
                                            <Building2 size={16} className="info-icon" color="#0288AC" />
                                            <div>
                                                <p className="info-label" style={{ color: '#0288AC', fontWeight: '700' }}>Room 2 (Companion Room)</p>
                                                <p className="info-value">{bookingData.secondRoom.name} ({bookingData.secondRoom.nights || bookingData.nightsCount}n)</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="receipt-card-section full-width">
                                    <h3>Payment Summary</h3>
                                    <div className="payment-summary-box">
                                        <div className="payment-row">
                                            <span>Booking ID</span>
                                            <span className="bold">{bookingData.id}</span>
                                        </div>
                                        <div className="payment-row">
                                            <span>Number of Guests</span>
                                            <span className="bold">{bookingData.guests}</span>
                                        </div>
                                        {bookingData.nightsCount && (
                                            <div className="payment-row">
                                                <span>Nights</span>
                                                <span className="bold">{bookingData.nightsCount}</span>
                                            </div>
                                        )}
                                        {bookingData.baseAmount && (
                                            <div className="payment-row">
                                                <span>Room Cost</span>
                                                <span className="bold">${parseFloat(bookingData.baseAmount).toFixed(2)}</span>
                                            </div>
                                        )}

                                        {/* Add-on Services */}
                                        {bookingData.addOns && bookingData.addOns.length > 0 && (
                                            <>
                                                <div className="payment-divider"></div>
                                                <p style={{ fontWeight: '700', fontSize: '13px', color: '#6366f1', marginBottom: '8px' }}>Add-on Services</p>
                                                {bookingData.addOns.map((addon, idx) => (
                                                    <div key={idx} className="payment-row" style={{ paddingLeft: '12px' }}>
                                                        <span>✓ {addon.name || addon.service_name || addon}</span>
                                                        <span className="bold">
                                                            {(addon.price_at_booking || addon.price) ? `+$${parseFloat(addon.price_at_booking || addon.price).toFixed(2)}` : 'Included'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </>
                                        )}

                                        <div className="payment-divider"></div>
                                        {bookingData.secondRoom && (
                                            <div className="payment-row">
                                                <span>Room 2 Cost</span>
                                                <span className="bold">+${parseFloat(bookingData.secondRoom.price_at_booking || 0).toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="payment-divider"></div>
                                        <div className="payment-row total-row">
                                            <span>Total Booking Value</span>
                                            <span className="total-value">{bookingData.totalAmount}</span>
                                        </div>
                                        <div className="payment-row">
                                            <span style={{ color: '#059669', fontWeight: '700' }}>Deposit / Amount Paid</span>
                                            <span className="bold" style={{ color: '#059669' }}>{bookingData.amountPaid}</span>
                                        </div>
                                        {bookingData.paymentPlan === 'Partial' && (
                                            <div className="payment-row" style={{ backgroundColor: '#FEF2F2', padding: '10px 12px', borderRadius: '8px', border: '1px solid #FCA5A5', marginTop: '8px' }}>
                                                <span style={{ color: '#991B1B', fontWeight: '800' }}>Balance to Collect at Check-In</span>
                                                <span className="bold" style={{ color: '#DC2626', fontSize: '17px' }}>{bookingData.remainingBalance}</span>
                                            </div>
                                        )}
                                        <div className="payment-row" style={{ color: '#ef4444', marginTop: '8px' }}>
                                            <span>App Commission ($0.99/night)</span>
                                            <span className="bold">-${(0.99 * (bookingData.nightsCount || 1)).toFixed(2)}</span>
                                        </div>
                                        <div className="payment-divider"></div>
                                        <div className="payment-row" style={{ fontSize: '18px', fontWeight: '800', color: '#10b981' }}>
                                            <span>Your Payout</span>
                                            <span>${(parseFloat(bookingData.totalAmount.replace('$', '')) - (0.99 * (bookingData.nightsCount || 1))).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="action-footer">
                                {!isCheckedIn ? (
                                    <button className="confirm-checkin-btn" onClick={handleCheckIn}>
                                        <CheckCircle size={18} /> Confirm & Check In
                                    </button>
                                ) : (
                                    <div className="success-message">
                                        <CheckCircle size={20} /> Guest Successfully Checked In!
                                    </div>
                                )}

                                {bookingData.paymentStatus !== 'Paid' ? (
                                    <button 
                                        className="confirm-checkin-btn" 
                                        onClick={handleMarkAsPaid}
                                        disabled={isMarkingPaid}
                                        style={{ background: 'linear-gradient(135deg, #059669, #047857)', marginTop: 12 }}
                                    >
                                        <CreditCard size={18} /> {isMarkingPaid ? 'Processing...' : 'Mark as Paid'}
                                    </button>
                                ) : (
                                    <div className="success-message" style={{ marginTop: 12, background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', color: '#15803d' }}>
                                        <CheckCircle size={20} /> Payment Confirmed
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default VerifyBooking;
